import React, { useCallback, useEffect, useState } from "react";
import { Form, Spinner } from "react-bootstrap";
import OtpInput from "react-otp-input";
import { $crud } from "../helper/CrudFactory";
import LanguageContext from "../helper/LanguageContext";
import { methodLabel, twoFactorRequest } from "../helper/twoFactorApi";
import { SettingsBanner, SettingsNote, SettingsRow, SettingsSection, SettingsTag } from "./settings/SettingsList";
import "./TwoFactorSettingsPanel.css";

/** Material glyph per method (mobile TwoFactorPro): authenticator, SMS, e-mail. */
const methodGlyph = (type) => (type === "email" ? "mail" : type === "sms" ? "sms" : "phonelink_lock");

const OTP_INPUT_STYLE = {
  height: 42,
  width: 36,
  margin: "0 4px",
  fontSize: 18,
  fontWeight: 600,
  color: "#303030",
  border: "1.5px solid #d9d9d9",
  borderRadius: 8,
  outline: "none",
  textAlign: "center",
};

function CodeBoxes({ value, onChange, onComplete }) {
  return (
    <div className="twofa-otp-row twofa-otp-row--settings">
      <OtpInput
        value={value}
        onChange={(next) => {
          const digits = String(next || "").replace(/\D/g, "").slice(0, 6);
          onChange(digits);
          if (digits.length === 6 && onComplete) onComplete(digits);
        }}
        numInputs={6}
        isInputNum
        separator={<span className="two-factor-settings-panel__otp-separator" />}
        inputStyle={OTP_INPUT_STYLE}
      />
    </div>
  );
}

function BackupCodesView({ codes, strings, onDone }) {
  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(codes.join("\n"));
      $crud.notify({ type: "success", message: strings.twofa_codes_copied || "Backup codes copied" });
    } catch (e) {
      $crud.notify({ type: "error", message: strings.twofa_something_wrong || "Something went wrong. Please try again." });
    }
  };
  return (
    <div className="twofa-panel kj-settings-card twofa-pro__card">
      <p className="twofa-settings-lead">{strings.twofa_backup_help || "Save these one-time codes in a safe place. Each code can be used once if you lose your phone."}</p>
      <div className="twofa-backup-box">
        {codes.map((item) => (
          <div key={item} className="twofa-backup-code">{item}</div>
        ))}
      </div>
      <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm twofa-link--left" onClick={copyAll}>
        {strings.twofa_copy_codes || "Copy codes"}
      </button>
      <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" onClick={onDone}>
        {strings.ok || "OK"}
      </button>
    </div>
  );
}

/**
 * Two-factor settings (mobile TwoFactorSettingsPro): on banner or intro, the methods
 * with their state, the default method, and turning it off (asked first).
 * `onStatusChange(enabled)` keeps the settings row "An / Aus" current.
 */
export default function TwoFactorSettingsPanel({ strings, onStatusChange }) {
  const { isGerm } = React.useContext(LanguageContext);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [view, setView] = useState("home");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);

  const [totpSecret, setTotpSecret] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [totpToken, setTotpToken] = useState("");
  const [totpCode, setTotpCode] = useState("");

  const [mobileNo, setMobileNo] = useState("+");
  const [smsToken, setSmsToken] = useState("");
  const [smsHint, setSmsHint] = useState("");
  const [smsCode, setSmsCode] = useState("");

  const [emailToken, setEmailToken] = useState("");
  const [emailHint, setEmailHint] = useState("");
  const [emailCode, setEmailCode] = useState("");

  const [password, setPassword] = useState("");
  const [stepCode, setStepCode] = useState("");
  const [stepToken, setStepToken] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    const res = await twoFactorRequest("get", "/user/2fa");
    if (res && res.status) setStatus(res.twoFactor);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (status && typeof onStatusChange === "function") onStatusChange(!!status.enabled);
    // Only when the status itself changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const notifyOk = (res, fallback) => {
    $crud.notify({ type: "success", message: (res && res.message) || fallback });
  };

  const notifyFail = (res, fallback) => {
    $crud.notify({
      type: "error",
      message: (res && res.message) || fallback || strings.twofa_something_wrong || "Something went wrong. Please try again.",
    });
  };

  const afterConfirm = async (res) => {
    if (!res || !res.status) {
      notifyFail(res, strings.twofa_invalid_code);
      return;
    }
    if (res.twoFactor) setStatus(res.twoFactor);
    if (res.backupCodes && res.backupCodes.length) {
      setBackupCodes(res.backupCodes);
      setView("backup");
    } else {
      notifyOk(res, strings.twofa_enabled || "Two-factor authentication is now on.");
      setView("home");
    }
  };

  const openTotp = async () => {
    setView("totp");
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/totp/setup");
    setBusy(false);
    if (!res || !res.status) {
      notifyFail(res);
      setView("home");
      return;
    }
    setTotpSecret(res.secret || "");
    setQrDataUrl(res.qrDataUrl || "");
    setTotpToken(res.challengeToken || "");
    setTotpCode("");
  };

  const confirmTotp = async (value) => {
    const next = String(value || totpCode || "").trim();
    if (next.length < 6) return;
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/totp/confirm", {
      code: next,
      challengeToken: totpToken,
    });
    setBusy(false);
    await afterConfirm(res);
  };

  const sendSms = async () => {
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/sms/setup", { mobileNo: mobileNo.trim() });
    setBusy(false);
    if (!res || !res.status) {
      notifyFail(res);
      return;
    }
    setSmsToken(res.challengeToken || "");
    setSmsHint(res.hint || "");
    notifyOk(res, strings.twofa_sent || "We sent a code.");
  };

  const confirmSms = async (value) => {
    const next = String(value || smsCode || "").trim();
    if (next.length < 6) return;
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/sms/confirm", {
      code: next,
      challengeToken: smsToken,
    });
    setBusy(false);
    await afterConfirm(res);
  };

  const sendEmail = async () => {
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/email/setup");
    setBusy(false);
    if (!res || !res.status) {
      notifyFail(res);
      return;
    }
    setEmailToken(res.challengeToken || "");
    setEmailHint(res.hint || "");
    notifyOk(res, strings.twofa_sent || "We sent a code.");
  };

  const confirmEmail = async (value) => {
    const next = String(value || emailCode || "").trim();
    if (next.length < 6) return;
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/email/confirm", {
      code: next,
      challengeToken: emailToken,
    });
    setBusy(false);
    await afterConfirm(res);
  };

  const onSetPrimary = async (method) => {
    const res = await twoFactorRequest("put", "/user/2fa/primary", { method });
    if (res && res.status) {
      setStatus(res.twoFactor);
      notifyOk(res, strings.twofa_primary_updated || "Default verification method updated.");
    } else {
      notifyFail(res);
    }
  };

  const onSendStepCode = async () => {
    const res = await twoFactorRequest("post", "/user/2fa/send-code", {
      method: status && status.primaryMethod,
    });
    if (res && res.status) {
      setStepToken(res.challengeToken || "");
      notifyOk(res, strings.twofa_sent || "We sent a code.");
    } else {
      notifyFail(res);
    }
  };

  const onDisable = async () => {
    if (!password || !stepCode) {
      notifyFail({}, strings.twofa_disable_need || "Enter your password and a verification code.");
      return;
    }
    setBusy(true);
    const res = await twoFactorRequest("post", "/user/2fa/disable", {
      password,
      code: stepCode,
      challengeToken: stepToken,
    });
    setBusy(false);
    if (res && res.status) {
      setStatus(res.twoFactor);
      setPassword("");
      setStepCode("");
      setStepToken("");
      notifyOk(res, strings.twofa_disabled || "Two-factor authentication is now off.");
    } else {
      notifyFail(res);
    }
  };

  if (loading) {
    return (
      <div className="twofa-panel twofa-panel--center">
        <Spinner animation="border" size="sm" />
      </div>
    );
  }

  if (view === "backup") {
    return (
      <BackupCodesView
        codes={backupCodes}
        strings={strings}
        onDone={() => {
          setView("home");
          load();
        }}
      />
    );
  }

  if (view === "totp") {
    return (
      <div className="twofa-panel kj-settings-card twofa-pro__card">
        <p className="twofa-settings-lead">
          {strings.twofa_totp_help || "Scan this QR code with Google Authenticator, Authy, or 1Password. You can also enter the key manually."}
        </p>
        {busy && !qrDataUrl ? <Spinner animation="border" size="sm" /> : null}
        {qrDataUrl ? <img className="twofa-qr" src={qrDataUrl} alt="" /> : null}
        {totpSecret ? (
          <button
            type="button"
            className="twofa-secret"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(totpSecret);
                $crud.notify({ type: "success", message: strings.twofa_secret_copied || "Secret key copied" });
              } catch (e) {}
            }}
          >
            {totpSecret}
          </button>
        ) : null}
        <p className="twofa-settings-lead">{strings.twofa_enter_app || "Enter the 6-digit code from your authenticator app."}</p>
        <CodeBoxes value={totpCode} onChange={setTotpCode} onComplete={confirmTotp} />
        <div className="twofa-actions">
          <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" disabled={busy || totpCode.length !== 6} onClick={() => confirmTotp(totpCode)}>
            {busy ? <Spinner animation="border" size="sm" /> : (strings.confirm || "Confirm")}
          </button>
          <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm" onClick={() => setView("home")}>{strings.back || "Back"}</button>
        </div>
      </div>
    );
  }

  if (view === "sms") {
    return (
      <div className="twofa-panel kj-settings-card twofa-pro__card">
        <p className="twofa-settings-lead">
          {strings.twofa_sms_help || "Enter your mobile number in international format. We will text a 6-digit code."}
        </p>
        <Form.Control
          className="mb-2"
          value={mobileNo}
          onChange={(e) => setMobileNo(e.target.value)}
          placeholder="+491701234567"
        />
        <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" disabled={busy} onClick={sendSms}>
          {busy && !smsToken ? <Spinner animation="border" size="sm" /> : (strings.twofa_send_code || "Send a code")}
        </button>
        {smsToken ? (
          <>
            <p className="twofa-settings-lead mt-3">
              {strings.twofa_enter_sms || "Enter the 6-digit code we sent by text message."}
              {smsHint ? ` ${smsHint}` : ""}
            </p>
            <CodeBoxes value={smsCode} onChange={setSmsCode} onComplete={confirmSms} />
            <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" disabled={busy || smsCode.length !== 6} onClick={() => confirmSms(smsCode)}>
              {busy ? <Spinner animation="border" size="sm" /> : (strings.confirm || "Confirm")}
            </button>
          </>
        ) : null}
        <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm twofa-link--left" onClick={() => setView("home")}>{strings.back || "Back"}</button>
      </div>
    );
  }

  if (view === "email") {
    return (
      <div className="twofa-panel kj-settings-card twofa-pro__card">
        <p className="twofa-settings-lead">
          {strings.twofa_email_help || "We will email a 6-digit code to the address on your account."}
        </p>
        <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" disabled={busy} onClick={sendEmail}>
          {busy && !emailToken ? <Spinner animation="border" size="sm" /> : (emailToken ? (strings.twofa_resend || "Resend code") : (strings.twofa_send_code || "Send a code"))}
        </button>
        {emailToken ? (
          <>
            <p className="twofa-settings-lead mt-3">
              {strings.twofa_enter_email || "Enter the 6-digit code we sent to your email."}
              {emailHint ? ` ${emailHint}` : ""}
            </p>
            <CodeBoxes value={emailCode} onChange={setEmailCode} onComplete={confirmEmail} />
            <button type="button" className="kj-btn kj-btn--outline kj-btn--on-surface" disabled={busy || emailCode.length !== 6} onClick={() => confirmEmail(emailCode)}>
              {busy ? <Spinner animation="border" size="sm" /> : (strings.confirm || "Confirm")}
            </button>
          </>
        ) : null}
        <button type="button" className="kj-btn kj-btn--ghost kj-btn--sm twofa-link--left" onClick={() => setView("home")}>{strings.back || "Back"}</button>
      </div>
    );
  }

  const methods = (status && status.methods) || [];
  const enabled = !!(status && status.enabled);

  const openSms = () => {
    if (status && status.smsConfigured === false && !status.smsEnabled) {
      notifyFail({}, strings.twofa_sms_unavailable);
      return;
    }
    setSmsToken("");
    setSmsCode("");
    setView("sms");
  };

  // "Aktiv" tag when a method is on, else "Hinzufügen" in the accent (mobile trailing).
  const methodTrailing = (isOn) => (isOn
    ? <SettingsTag tone="ok">{strings.twofa_on || "On"}</SettingsTag>
    : <span className="twofa-pro__add">{strings.twofa_add || "Add"}</span>);

  // Turning it off asks first (mobile GlassDialog "Zwei-Faktor-Authentifizierung ausschalten?").
  const confirmDisable = async () => {
    try {
      await $crud.confirm({
        title: strings.v2_auth_twofa_off_confirm || (isGerm ? "Zwei-Faktor-Authentifizierung ausschalten?" : "Turn off two-factor authentication?"),
        textContent: strings.twofa_disable_hint || "Enter your password and a current code to turn two-factor authentication off.",
        options: {
          ok: strings.twofa_turn_off || "Turn off",
          cancel: strings.cancel || "Cancel",
        },
      });
    } catch (_) {
      return;
    }
    onDisable();
  };

  return (
    <div className="twofa-pro">
      {enabled ? (
        <SettingsBanner
          tone="ok"
          icon="verified_user"
          title={strings.twofa_on || "On"}
          body={strings.twofa_on_desc || "Two-factor authentication is on. You will need a code each time you sign in."}
        />
      ) : (
        <SettingsNote className="twofa-pro__intro">
          {strings.twofa_off_desc || "Add a second step after your password. Authenticator apps are the most secure option."}
        </SettingsNote>
      )}

      <SettingsSection caption={strings.twofa_methods || "Methods"}>
        <SettingsRow
          icon="phonelink_lock"
          label={strings.twofa_method_totp || "Authenticator app"}
          trailing={methodTrailing(status && status.totpEnabled)}
          onClick={openTotp}
        />
        <SettingsRow
          icon="sms"
          label={strings.twofa_method_sms || "Text message"}
          sub={status && status.smsEnabled && status.mobileNo ? status.mobileNo : undefined}
          trailing={methodTrailing(status && status.smsEnabled)}
          onClick={openSms}
        />
        {status && status.emailAvailable === false ? null : (
          <SettingsRow
            icon="mail"
            label={strings.twofa_method_email || "Email"}
            sub={status && status.emailEnabled && status.emailHint ? status.emailHint : undefined}
            trailing={methodTrailing(status && status.emailEnabled)}
            onClick={() => {
              setEmailToken("");
              setEmailCode("");
              setView("email");
            }}
          />
        )}
      </SettingsSection>

      {enabled && methods.length > 1 ? (
        <section className="kj-settings-section">
          <h3 className="kj-settings-caption">{strings.twofa_default_method || "Default method"}</h3>
          <div className="kj-settings-card" role="radiogroup" aria-label={strings.twofa_default_method || "Default method"}>
            {methods.map((item) => {
              const selected = status.primaryMethod === item.type;
              return (
                <SettingsRow
                  key={item.type}
                  icon={methodGlyph(item.type)}
                  label={methodLabel(item.type, strings)}
                  sub={selected ? (strings.twofa_default || "Default") : undefined}
                  chevron={false}
                  className={selected ? "twofa-pro__choice is-selected" : "twofa-pro__choice"}
                  trailing={(
                    <span className={`twofa-pro__pick${selected ? " is-on" : ""}`} aria-hidden="true">
                      {selected ? <span className="material-symbols-outlined">check</span> : null}
                    </span>
                  )}
                  onClick={() => {
                    if (!selected) onSetPrimary(item.type);
                  }}
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {enabled ? (
        <section className="kj-settings-section">
          <h3 className="kj-settings-caption">{strings.twofa_turn_off || "Turn off"}</h3>
          <div className="kj-settings-card twofa-pro__form">
            <p className="twofa-pro__note">
              {strings.twofa_disable_hint || "Enter your password and a current code to turn two-factor authentication off."}
            </p>
            <label className="twofa-pro__field">
              <span className="twofa-pro__label">{strings.password || "Password"}</span>
              <span className="twofa-pro__input">
                <span className="material-symbols-outlined" aria-hidden="true">lock</span>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </span>
            </label>
            <label className="twofa-pro__field">
              <span className="twofa-pro__label">{strings.twofa_code_placeholder || "6-digit code or backup code"}</span>
              <span className="twofa-pro__input">
                <span className="material-symbols-outlined" aria-hidden="true">pin</span>
                <input
                  type="text"
                  autoComplete="one-time-code"
                  value={stepCode}
                  onChange={(e) => setStepCode(e.target.value)}
                />
              </span>
            </label>
            <div className="twofa-pro__actions">
              <button type="button" className="kj-btn kj-btn--secondary" onClick={onSendStepCode}>
                <span className="material-symbols-outlined" aria-hidden="true">send</span>
                <span>{strings.twofa_send_code || "Send a code"}</span>
              </button>
              <button
                type="button"
                className={`kj-btn kj-btn--primary kj-btn--danger${busy ? " kj-btn--loading" : ""}`}
                disabled={busy || !password || !stepCode}
                onClick={confirmDisable}
              >
                {strings.twofa_turn_off || "Turn off"}
              </button>
            </div>
          </div>
        </section>
      ) : null}
    </div>
  );
}
